import type { TableProps } from '@risk-smart/themed-cloudscape-components/table';
import { Badge, Button, Dialog } from '@risksmart-app/atomic-ui';
import Table from '@risksmart-app/components/src/table';
import type { GetWebhookDeliveriesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useListWebhookDeliveries } from '@/hooks/queries';
import { toLocalDateTime } from '@/utils/dateUtils';
import { EMPTY_CELL } from '@/utils/EmptyCell';

import WebhookDeliveryStatusBadge from '../WebhookDeliveryStatusBadge';

type DeliveryRow =
  GetWebhookDeliveriesQuery['organisation_webhook_delivery'][number];

interface Props {
  webhookId: string;
  webhookTitle: string;
  onDismiss: () => void;
}

const WebhookDeliveryLogsModal: FC<Props> = ({
  webhookId,
  webhookTitle,
  onDismiss,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'webhooks.deliveryLogs',
  });

  const { data, loading, error } = useListWebhookDeliveries({
    queryArgs: { webhookId },
  });

  const deliveries = data?.organisation_webhook_delivery ?? [];

  // Payload shows in a nested dialog; a popover would portal into Base UI's
  // inert layer and not be interactive. `null` = no row selected.
  const [openPayload, setOpenPayload] = useState<DeliveryRow | null>(null);

  const deliveryTypeLabel = (deliveryType: string): string => {
    switch (deliveryType) {
      case 'initial':
        return t('deliveryType.initial');
      case 'retry':
        return t('deliveryType.retry');
      case 'manual':
        return t('deliveryType.manual');
      default:
        return deliveryType;
    }
  };

  const columnDefinitions: TableProps.ColumnDefinition<DeliveryRow>[] = [
    {
      id: 'DispatchedAt',
      header: t('columns.dispatchedAt'),
      cell: (item) => toLocalDateTime(item.DispatchedAt) || EMPTY_CELL,
    },
    {
      id: 'EventType',
      header: t('columns.eventType'),
      cell: (item) => item.EventType,
    },
    {
      id: 'Status',
      header: t('columns.status'),
      cell: (item) => <WebhookDeliveryStatusBadge status={item.Status} />,
    },
    {
      id: 'DeliveryType',
      header: t('columns.deliveryType'),
      cell: (item) => (
        <Badge variant={'secondary'}>
          {deliveryTypeLabel(item.DeliveryType)}
        </Badge>
      ),
    },
    {
      id: 'HttpStatusCode',
      header: t('columns.httpStatusCode'),
      cell: (item) => item.HttpStatusCode ?? EMPTY_CELL,
    },
    {
      id: 'ResponseBodyPreview',
      header: t('columns.responseBodyPreview'),
      maxWidth: 320,
      cell: (item) => item.ResponseBodyPreview ?? EMPTY_CELL,
    },
    {
      id: 'ErrorMessage',
      header: t('columns.errorMessage'),
      maxWidth: 240,
      cell: (item) => item.ErrorMessage ?? EMPTY_CELL,
    },
    {
      id: 'Payload',
      header: t('columns.payload'),
      cell: (item) => (
        <Button
          variant={'neutral'}
          style={'ghost'}
          size={'sm'}
          onClick={() => setOpenPayload(item)}
        >
          {t('viewPayload')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Dialog
        open
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            onDismiss();
          }
        }}
        size={'xl'}
      >
        <Dialog.Header title={t('title', { title: webhookTitle })} />
        <Dialog.Body>
          <Table
            variant={'embedded'}
            loading={loading}
            items={deliveries}
            trackBy={'Id'}
            columnDefinitions={columnDefinitions}
            empty={
              <div style={{ textAlign: 'center' }}>
                {error ? t('loadError') : t('empty')}
              </div>
            }
          />
        </Dialog.Body>
      </Dialog>
      {openPayload ? (
        <Dialog
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setOpenPayload(null);
            }
          }}
          size={'lg'}
        >
          <Dialog.Header title={t('payloadHeading')} />
          <Dialog.Body>
            <pre
              style={{
                margin: 0,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.8125rem',
              }}
            >
              {JSON.stringify(openPayload.Payload, null, 2)}
            </pre>
          </Dialog.Body>
        </Dialog>
      ) : null}
    </>
  );
};

export default WebhookDeliveryLogsModal;
